import { useApolloClient } from '@apollo/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  SuppliersPageTaxJurisdictionListDocument,
  useSuppliersPageTaxJurisdictionListQuery,
  type SuppliersPageTaxJurisdictionListQuery,
  type SuppliersPageTaxJurisdictionListQueryVariables,
  type TaxJurisdictionListInput,
} from '../../../../../../../generated';
import {
  type TaxJurisdictionListResult,
  type TaxJurisdictionRow,
} from '../types';
import { mapTaxJurisdictionListResponse } from '../utils/taxJurisdictionMapper';

const PAGE_SIZE = 25;

interface UseTaxJurisdictionListParams {
  vendorId?: string;
}

interface UseTaxJurisdictionListResult {
  jurisdictions: TaxJurisdictionRow[];
  /** First page is loading, so there is still an empty table. */
  isLoading: boolean;
  /** A "Load more" page is loading. */
  isLoadingMore: boolean;
  errorMessage: string | null;
  /** A "Load more" page failed; the rows already loaded stay on screen. */
  loadMoreErrorMessage: string | null;
  hasNextPage: boolean;
  loadMore: () => void;
}

const LOAD_MORE_ERROR_MESSAGE =
  'Could not load more jurisdictions. Press Load more to try again.';

/**
 * Read-only list of a distributor's tax jurisdictions, accumulating pages for
 * a cursor-based "Load more".
 *
 * `no-cache`: the `TaxJurisdictionListResponse` union members have no `id`,
 * so InMemoryCache cannot normalise them and a cache read drops the
 * inline-fragment `edges`. Reading straight from the network keeps the
 * response intact, and "Load more" below reads the same way.
 */
export const useTaxJurisdictionList = ({
  vendorId,
}: UseTaxJurisdictionListParams): UseTaxJurisdictionListResult => {
  const client = useApolloClient();
  const enabled = Boolean(vendorId);

  const buildInput = useCallback(
    (cursor?: string): TaxJurisdictionListInput => ({
      vendorId: vendorId ?? '',
      pagination: { limit: PAGE_SIZE, direction: 'forward', cursor },
    }),
    [vendorId]
  );

  const { data, loading, error } = useSuppliersPageTaxJurisdictionListQuery({
    variables: { input: buildInput() },
    skip: !enabled,
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'all',
  });

  const mapped = useMemo(
    () => mapTaxJurisdictionListResponse(data?.taxJurisdictionList),
    [data]
  );

  const [jurisdictions, setJurisdictions] = useState<TaxJurisdictionRow[]>([]);
  const [pageInfo, setPageInfo] = useState<{
    hasNextPage: boolean;
    endCursor: string;
  } | null>(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [loadMoreErrorMessage, setLoadMoreErrorMessage] = useState<
    string | null
  >(null);
  const seededResult = useRef<TaxJurisdictionListResult | null>(null);
  // Bumped on every reset and read by an in-flight "Load more". A vendor id
  // comparison would let a request survive a move away and back, because the
  // id it started with matches again by the time it lands.
  const generation = useRef(0);

  // Drop the accumulated pages when the distributor changes. Clearing
  // `isLoadingMore` here keeps the button from spinning on forever.
  useEffect(() => {
    generation.current += 1;
    seededResult.current = null;
    setJurisdictions([]);
    setPageInfo(null);
    setIsLoadingMore(false);
    setLoadMoreErrorMessage(null);
  }, [vendorId]);

  // Seed page one on a fresh network completion. Gating on `!loading` ignores
  // Apollo's synchronous same-variable replay, so the fresh `no-cache`
  // response wins over a stale page 1.
  useEffect(() => {
    if (loading || mapped?.kind !== 'success') {
      return;
    }
    if (seededResult.current === mapped) {
      return;
    }
    seededResult.current = mapped;
    // Any accumulated "Load more" pages were relative to the previous page 1
    // and are dropped with it.
    setJurisdictions(mapped.page.jurisdictions);
    setPageInfo(mapped.page.pageInfo);
  }, [loading, mapped]);

  const loadMore = useCallback(() => {
    if (
      !enabled ||
      !pageInfo?.hasNextPage ||
      !pageInfo.endCursor ||
      isLoadingMore
    ) {
      return;
    }

    const cursor = pageInfo.endCursor;
    const requestGeneration = generation.current;
    setIsLoadingMore(true);
    setLoadMoreErrorMessage(null);

    client
      .query<
        SuppliersPageTaxJurisdictionListQuery,
        SuppliersPageTaxJurisdictionListQueryVariables
      >({
        query: SuppliersPageTaxJurisdictionListDocument,
        variables: { input: buildInput(cursor) },
        fetchPolicy: 'no-cache',
        errorPolicy: 'all',
      })
      .then((response) => {
        // Drop a stale page: the distributor changed while it was in flight,
        // and appending it would poison the list and the cursor.
        if (requestGeneration !== generation.current) {
          return;
        }
        const nextPage = mapTaxJurisdictionListResponse(
          response.data?.taxJurisdictionList
        );
        if (nextPage?.kind === 'success') {
          setJurisdictions((prev) => [...prev, ...nextPage.page.jurisdictions]);
          setPageInfo(nextPage.page.pageInfo);
          return;
        }
        // A failure member on a later page keeps the loaded rows and says why
        // the rest did not arrive.
        setLoadMoreErrorMessage(nextPage?.message ?? LOAD_MORE_ERROR_MESSAGE);
      })
      .catch(() => {
        if (requestGeneration === generation.current) {
          setLoadMoreErrorMessage(LOAD_MORE_ERROR_MESSAGE);
        }
      })
      .finally(() => {
        if (requestGeneration === generation.current) {
          setIsLoadingMore(false);
        }
      });
  }, [enabled, pageInfo, isLoadingMore, client, buildInput]);

  const errorMessage =
    mapped?.kind === 'error' ? mapped.message : (error?.message ?? null);

  const isLoading =
    enabled && loading && jurisdictions.length === 0 && !errorMessage;

  return {
    jurisdictions,
    isLoading,
    isLoadingMore,
    errorMessage,
    loadMoreErrorMessage,
    hasNextPage: Boolean(pageInfo?.hasNextPage),
    loadMore,
  };
};
